import { GroupType, PeoplePicker, PersonType } from "@microsoft/mgt-react";
import { isEqual } from "lodash";
import { FC, Fragment, useMemo, useState } from "react";
import { ExtraStateContext } from "..";
import { UserSelect } from "../../../../../../components/input/select/UserSelect";
import { approverType } from "../../../../../../constants/itCodeRequestConstants";
import { getDDMMYYYFormat } from "../../../../../../helper/dateTimeHelper";
import InformationList, { ColumnContent, LeftRightContent, ListContent } from "../../../../components/InformationList";
import TableModal from "../../../../components/detail";
import { useProcess } from "../../../../process-context";
import { FileAttachmentCard } from "../../../../upload";
import CommentSection from "../../../commentsection/comment-sections";
import { tableColumns } from "./table-column";
import { Button } from "react-bootstrap";
import FontFa from "../../../../../../components/fontFa/fontFa";
import EditRepairToolsModal from "../../../../modal/edit-repair-tool";
import ViewHistoryEditRepairToolsModal from "../../../../modal/view-history-update-repair";
import { useUser } from "../../../../../../hook";
import { useUpdateRTR } from "../../hooks/use-update-RTR";

interface Props {
  assignee: any;
  onApproverChange: (selectedOptions: any) => void;
  onEmployeeChange: (selectedOptions: any) => void;
  onAssignedChange: (selectedOptions: any) => void;
}

const FormContent: FC<Props> = (props) => {
  const { assignee, onApproverChange, onEmployeeChange, onAssignedChange } = props;
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [isOpenViewHistoryModal, setIsOpenViewHistoryModal] = useState(false);

  const { Request, approvalStep, approvalType, comment, approverOptions, setComment } = useProcess<ExtraStateContext>();

  const { fields } = Request;
  const { email, user } = useUser();
  const admin = [
    "thiennv@viags.vn",
    "trungdt@viags.vn",
    "canbo1@viags.vn",
    "hieppt@viags.vn",
    "phuocvd@viags.vn",
    "hantt@viags.vn",
    "lapdt@viags.vn",
    "quynhvtn@viags.vn",
    "trungnn@viags.vn",
  ];
  const isAdmin = admin.includes(email?.toLowerCase());
  const { updateRTRequest } = useUpdateRTR();

  const {
    eDeptHeadComment,
    eITHeadComment,
    eAssigneeComment,
    eApprovedBy,
    eApprovedByITHead,
    eAssignee,
    eRequestStatus,
  } = fields;

  const onEditModalOpen = () => {
    setIsOpenEditModal(true);
  };

  const onViewHistoryModalOpen = () => {
    setIsOpenViewHistoryModal(true);
  };

  const comments = useMemo(() => {
    return [
      {
        hidden: !eDeptHeadComment,
        content: `Comment của ${eApprovedBy.split("|")[1]}: ${eDeptHeadComment}`,
      },
      {
        hidden: !eITHeadComment,
        content: `Comment của ${eApprovedByITHead.split("|")[1]}: ${eITHeadComment}`,
      },
      {
        hidden: !eAssigneeComment,
        content: `Comment của ${eAssignee.split("|")[1]}: ${eAssigneeComment}`,
      },
      {
        hidden: approvalStep !== approvalType,
        content: (
          <div className="input">
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} />
          </div>
        ),
      },
    ];
  }, [
    eDeptHeadComment,
    eApprovedBy,
    eITHeadComment,
    eApprovedByITHead,
    eAssigneeComment,
    eAssignee,
    approvalStep,
    approvalType,
    comment,
    setComment,
  ]);

  const smColData = useMemo(() => {
    return [
      {
        content: (
          <LeftRightContent left="Người yêu cầu" right={<strong>{Request.createdBy.user.displayName}</strong>} />
        ),
      },
      {
        content: <LeftRightContent left="Cơ quan/Đơn vị" right={<strong>{Request.fields.eDepartment}</strong>} />,
      },
      {
        hidden: {
          sm: isEqual(Request.fields.eDepartment, Request.fields.eUnitRoom),
        },
        content: <LeftRightContent left="Đơn vị" right={<strong>{Request.fields.eUnitRoom}</strong>} />,
      },
      {
        hidden: {
          sm: isEqual(Request.fields.eUnion, Request.fields.eUnitRoom),
        },
        content: <LeftRightContent left="Đội/Tổ" right={<strong>{Request.fields.eUnion}</strong>} />,
      },
      {
        content: <LeftRightContent left="Mã TS/CC" right={<strong>{Request.fields.ECodeTCSSId}</strong>} />,
      },
      {
        content: <LeftRightContent left="Vị trí thiết bị" right={<strong>{Request.fields.eDeviceLocation}</strong>} />,
      },
      {
        content: (
          <LeftRightContent
            left="Ngày hỏng"
            right={
              <span>
                <strong>{getDDMMYYYFormat(Request.fields.eBrokenDate)}</strong>
                {isAdmin && (
                  <>
                    <Button
                      onClick={onEditModalOpen}
                      variant="outline"
                      className="mb-1"
                      title="Chỉnh sửa ngày tạo"
                      style={{ boxShadow: "none" }}
                    >
                      <FontFa name="faEdit" size="sm" color="#ffcc00" />
                    </Button>
                    <Button
                      onClick={onViewHistoryModalOpen}
                      variant="outline"
                      className="mb-1"
                      title="Xem lịch sử chỉnh sửa"
                      style={{ boxShadow: "none", padding: "0" }}
                    >
                      <FontFa name="faHistory" size="sm" color="#34e5eb" />
                    </Button>
                  </>
                )}
              </span>
            }
            smColumn={false}
          />
        ),
      },
      {
        content: (
          <LeftRightContent
            left="Ngày tăng"
            right={<strong>{Request.fields.eDayOfIncrease}</strong>}
            smColumn={false}
          />
        ),
      },
      {
        content: (
          <LeftRightContent left="Nguyên giá" right={<strong>{Request.fields.eCost}</strong>} smColumn={false} />
        ),
      },
      {
        content: (
          <LeftRightContent
            left="Giá trị còn lại"
            right={<strong>{Request.fields.eResidualValue}</strong>}
            smColumn={false}
          />
        ),
      },
      {
        content: (
          <LeftRightContent
            left="Lần sửa trong năm"
            right={<strong>{Request.fields.eNumEditsInYear}</strong>}
            smColumn={false}
          />
        ),
      },
      {
        content: (
          <LeftRightContent
            left="Lần sửa gần nhất"
            right={<strong>{Request.fields.eLastEdit}</strong>}
            smColumn={false}
          />
        ),
      },
      {
        content: (
          <LeftRightContent left="Hiện tượng hỏng" right={<strong>{Request.fields.eCorruptedSymptoms}</strong>} />
        ),
      },
      {
        content: (
          <LeftRightContent left="Độ ưu tiên" right={<strong>{Request.fields.ePriority}</strong>} smColumn={false} />
        ),
      },
      {
        content: <LeftRightContent left="Nguyên nhân" right={<strong>{Request.fields.eCause}</strong>} />,
      },
      {
        content: (
          <LeftRightContent
            left="Phương án đề xuất"
            right={<strong>{Request.fields.eRepair}</strong>}
            smColumn={false}
          />
        ),
      },
    ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Request]);

  const fullColData = useMemo(() => {
    return [
      {
        content: <TableModal data={JSON.parse(Request.fields.eContent)} columns={tableColumns} />,
      },
      {
        content: (
          <Fragment>
            Tệp đính kèm
            <div className="mt-2 d-flex flex-column">
              <FileAttachmentCard
                listFile={Request.fields.eFileContent ? JSON.parse(Request.fields.eFileContent) : []}
              />
            </div>
          </Fragment>
        ),
      },
      {
        content: (
          <ListContent
            label="Comments"
            data={comments}
            getItemHidden={(item) => item.hidden}
            renderItemContent={(item) => item.content}
          />
        ),
      },
      {
        content: (
          <Fragment>
            Thông tin trao đổi
            <div className="mt-2">
              <CommentSection comments={[]} data={Request} user={user} id={3} className="mb-4" />
            </div>
          </Fragment>
        ),
      },
      {
        hidden: approvalType !== approverType.itlead || approvalStep !== approvalType,
        content: (
          <ColumnContent
            label="Cán bộ/Chuyên viên CNTT thực hiện"
            content={
              <div className="people_picker-custom-detail">
                <PeoplePicker
                  className="people-picker"
                  placeholder="* Vui lòng chọn cán bộ thực hiện"
                  type={PersonType.any}
                  selectionChanged={onEmployeeChange}
                />
              </div>
            }
          />
        ),
      },
      {
        hidden:
          approvalType !== approverType.approver || approvalStep !== approvalType || eRequestStatus === "Completed",
        content: (
          <ColumnContent
            label={
              <div className="mx-2 py-2 d-flex column">
                <h5>Chọn người thực hiện</h5>
                <span className="text-danger">*</span>
              </div>
            }
            content={
              <div className="people_picker-custom-detail">
                <PeoplePicker
                  className="people-picker"
                  placeholder="Vui lòng chọn người thực hiện"
                  type={PersonType.any}
                  groupType={GroupType.distribution | GroupType.unified}
                  selectionChanged={onEmployeeChange}
                />
              </div>
            }
          />
        ),
      },
      {
        hidden: approvalType !== approverType.itlead || approvalStep !== approvalType,
        content: (
          <ColumnContent
            label="Cán bộ hoàn tất nghiệm thu"
            content={
              <div className="people_picker-custom-detail">
                <UserSelect
                  name="Chọn cán bộ hoàn tất nghiệm thu"
                  onTouched={() => {
                    return;
                  }}
                  placeholder="Chọn cán bộ hoàn tất nghiệm thu"
                  options={approverOptions}
                  value={assignee}
                  onChange={onApproverChange}
                />
              </div>
            }
          />
        ),
      },
      {
        hidden: approvalType !== approverType.ithead || approvalStep !== approvalType,
        content: (
          <ColumnContent
            label="Cán bộ hoàn tất nghiệm thu"
            content={
              <div className="people_picker-custom-detail">
                <PeoplePicker
                  className="people-picker"
                  placeholder="Vui lòng chọn cán bộ hoàn tất nghiệm thu"
                  type={PersonType.any}
                  selectionChanged={onAssignedChange}
                />
              </div>
            }
          />
        ),
      },
    ];
  }, [
    Request,
    comments,
    user,
    approvalType,
    approvalStep,
    onEmployeeChange,
    eRequestStatus,
    approverOptions,
    assignee,
    onApproverChange,
    onAssignedChange,
  ]);

  const onEditModalClose = () => {
    setIsOpenEditModal(!isOpenEditModal);
  };

  const onViewHistoryModalClose = () => {
    setIsOpenViewHistoryModal(!isOpenViewHistoryModal);
  };

  const handleDateChange = async (date: Date) => {
    const historyList = Request.fields.eUpdateHistory ? JSON.parse(Request.fields.eUpdateHistory) : [];
    const createdHistory = {
      id: "broken",
      value: date,
      modifiedDate: new Date(),
      user: user.current.name,
    };
    historyList.push(createdHistory);
    const fields = {
      eBrokenDate: date,
      eUpdateHistory: JSON.stringify(historyList),
    };
    updateRTRequest(
      { id: Request.id, body: fields },
      { successText: "Chỉnh sửa ngày hỏng thành công", errorText: "Chỉnh sửa ngày hỏng thất bại" },
    );
  };

  return (
    <Fragment>
      <InformationList
        size="sm-col"
        data={smColData}
        renderContent={(item) => item.content}
        getItemHidden={(item) => item.hidden}
      />
      <InformationList
        size="full-col"
        data={fullColData}
        getItemHidden={(item) => item.hidden}
        renderContent={(item) => item.content}
      />
      <ViewHistoryEditRepairToolsModal
        isOpen={isOpenViewHistoryModal}
        onClose={onViewHistoryModalClose}
        value={Request.fields.eUpdateHistory}
        id="broken"
      />
      <EditRepairToolsModal
        isOpen={isOpenEditModal}
        onClose={onEditModalClose}
        onDateChange={handleDateChange}
        value={Request.fields.eBrokenDate}
        isShowTime={false}
      />
    </Fragment>
  );
};

export default FormContent;
